import { useState } from 'react';
import PropTypes from 'prop-types';
import Badge from '@mui/material/Badge';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import NotificationPanel from './NotificationPanel';
import { useNotificationSummary } from '../../../hooks/useNotificationLog';

// Kept as its own component so Footer itself never subscribes to the store: a
// push re-renders this four-node subtree, never the 624-line footer. It also
// owns its own open state, exactly like FooterTogglesMenu, so mounting it costs
// Footer no new props and AppRoutes no new footerProps entries.

// Blue is the resting badge colour on purpose. Per the AE design system
// #4EC2F5 means "you are here / passive highlight", which is precisely what an
// unread counter is. #FCC144 yellow means "required action" — using it for
// "Preview URL copied" would train operators to ignore yellow, and yellow is
// the one colour that cannot afford to be ignored.
const UNREAD_BLUE = '#4EC2F5';

const FooterNotificationBell = ({ compactStatus = false }) => {
  const [anchor, setAnchor] = useState(null);
  const { unread, topSeverity } = useNotificationSummary();
  const open = Boolean(anchor);

  const hasUnread = unread > 0;
  const Icon = hasUnread ? NotificationsActiveRoundedIcon : NotificationsNoneRoundedIcon;

  let badgeColor;
  let badgeSx;
  if (topSeverity === 'error') badgeColor = 'error';
  else if (topSeverity === 'warning') badgeColor = 'warning';
  else badgeSx = { bgcolor: UNREAD_BLUE, color: '#040404' };

  let iconColor = 'rgba(255,255,255,0.6)';
  if (open) iconColor = '#FEEC67';
  else if (topSeverity === 'error') iconColor = '#FF6B6B';

  const tooltip = hasUnread
    ? `${unread} new notification${unread === 1 ? '' : 's'} — click to view`
    : 'Notifications — nothing new';

  return (
    <>
      <Tooltip title={tooltip}>
        <IconButton
          onClick={(event) => setAnchor(open ? null : event.currentTarget)}
          size="small"
          // Asserting on a computed colour is brittle; asserting on this is not.
          data-severity={topSeverity || 'none'}
          sx={{
            color: iconColor,
            transition: 'color 0.2s ease',
            '&:hover': { color: '#FEEC67' },
          }}
        >
          <Badge
            // Remounting on every count change replays the pulse below. It is a
            // four-node leaf, so this is cheaper than any state-driven restart.
            key={unread}
            badgeContent={unread}
            color={badgeColor}
            overlap="circular"
            sx={{
              // The whole point of this ticket is that nothing overlays the app,
              // so the badge is the only thing telling an operator watching the
              // pitch that a save failed. Motion is reserved for errors: a bell
              // that twitches for "Clock START command sent" is wallpaper inside
              // a minute, and then it signals nothing at all.
              //
              // opacity + transform only, so it runs on the compositor — no
              // layout, no paint, and no React renders for as long as it lasts.
              '@keyframes aedriveBellPulse': {
                '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                '50%': { opacity: 0.55, transform: 'scale(1.12)' },
              },
              '& .MuiBadge-badge': {
                fontSize: 10,
                height: 16,
                minWidth: 16,
                fontWeight: 700,
                ...badgeSx,
                ...(topSeverity === 'error' && {
                  animation: 'aedriveBellPulse 1.4s ease-in-out infinite',
                  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                }),
              },
            }}
          >
            <Icon sx={{ fontSize: compactStatus ? 22 : 18 }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <NotificationPanel open={open} anchorEl={anchor} onClose={() => setAnchor(null)} />
    </>
  );
};

FooterNotificationBell.propTypes = {
  compactStatus: PropTypes.bool,
};

export default FooterNotificationBell;
